import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import mapboxgl from "mapbox-gl";
import { makeStyles } from "@material-ui/core/styles";
import { getAssetTypeColor } from "../../constants/plotColors";

import "mapbox-gl/dist/mapbox-gl.css";

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: 0,
  },
  map: {
    position: "absolute",
    inset: 0,
  },
  clusterToggle: {
    position: "absolute",
    top: 12,
    left: 12,
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 10px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
    color: "#334155",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
  },
  empty: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    color: "#cbd5e1",
    background: "linear-gradient(135deg, rgba(15,23,42,0.92), rgba(30,41,59,0.88))",
    textAlign: "center",
    padding: 16,
  },
  emptyInner: {
    maxWidth: 320,
    fontSize: 13,
    lineHeight: 1.7,
  },
}));

const DEFAULT_CENTER = [139.767125, 35.681236];
const DEFAULT_ZOOM = 11.7;
const MAP_STYLE_URL = "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb";
const SOURCE_ID = "plot-source";
const ALL_SOURCE_ID = "plot-source-all";
const CLUSTER_LAYER_ID = "plot-clusters";
const CLUSTER_COUNT_LAYER_ID = "plot-cluster-count";
const POINT_LAYER_ID = "plot-points";
const ALL_POINT_LAYER_ID = "plot-all-points";
const ALL_POINT_LABEL_LAYER_ID = "plot-all-point-labels";
const POINT_LABEL_LAYER_ID = "plot-point-labels";

const getBounds = (plots) => {
  if (!plots || plots.length === 0) {
    return null;
  }

  return plots.reduce(
    (bounds, plot) => bounds.extend([plot.lng, plot.lat]),
    new mapboxgl.LngLatBounds([plots[0].lng, plots[0].lat], [plots[0].lng, plots[0].lat])
  );
};

const getAssetLabel = (assetType) => String(assetType || "不明")
  .replace(/\s*[（(][^）)]*[）)]/g, "")
  .trim();

const buildFeatureCollection = (plots) => ({
  type: "FeatureCollection",
  features: (plots || [])
    .filter((plot) => Number.isFinite(Number(plot.lng)) && Number.isFinite(Number(plot.lat)))
    .map((plot) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [Number(plot.lng), Number(plot.lat)],
      },
      properties: {
        id: String(plot.id || ""),
        plotId: String(plot.plotId || plot.id || ""),
        municipality: String(plot.municipality || "不明"),
        assetType: String(plot.assetType || "不明"),
        assetLabel: getAssetLabel(plot.assetType),
        assetColor: getAssetTypeColor(String(plot.assetType || "不明")),
        lv: Number(plot.lv || 1),
      },
    })),
});

const buildClusterCountExpression = (totalCount) => [
  "format",
  ["get", "point_count_abbreviated"],
  { "font-scale": 1.08 },
  "\n",
  {},
  [
    "number-format",
    ["*", ["/", ["get", "point_count"], Math.max(totalCount, 1)], 100],
    { "min-fraction-digits": 1, "max-fraction-digits": 1 },
  ],
  { "font-scale": 0.72 },
  "%",
];

const PlotMapboxMap = ({
  plots,
  onSelectPlot,
  panelOpen,
  initialCenter,
  initialZoom,
}) => {
  const classes = useStyles();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [clusterEnabled, setClusterEnabled] = useState(true);
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  const center = useMemo(() => initialCenter || DEFAULT_CENTER, [initialCenter]);
  const zoom = initialZoom || DEFAULT_ZOOM;
  const featureCollection = useMemo(() => buildFeatureCollection(plots), [plots]);
  const clusterCountExpression = useMemo(
    () => buildClusterCountExpression(featureCollection.features.length),
    [featureCollection.features.length]
  );
  const plotsById = useMemo(() => {
    const map = new Map();
    (plots || []).forEach((plot) => map.set(String(plot.id), plot));
    return map;
  }, [plots]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !token) {
      return undefined;
    }

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center,
      zoom,
      localIdeographFontFamily: "'Noto Sans JP', 'Hiragino Sans', 'Yu Gothic', sans-serif",
      attributionControl: true,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.on("load", () => {
      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: featureCollection,
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 50,
      });
      map.addSource(ALL_SOURCE_ID, {
        type: "geojson",
        data: featureCollection,
      });

      map.addLayer({
        id: CLUSTER_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#60a5fa",
          "circle-radius": [
            "step",
            ["get", "point_count"],
            22,
            25,
            30,
            100,
            38,
          ],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });

      map.addLayer({
        id: CLUSTER_COUNT_LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        layout: {
          "text-field": clusterCountExpression,
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": 14,
        },
        paint: {
          "text-color": "#ffffff",
        },
      });

      map.addLayer({
        id: POINT_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": ["get", "assetColor"],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 4, 14, 7, 18, 9],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.5,
          "circle-opacity": 0.9,
        },
      });

      map.addLayer({
        id: ALL_POINT_LAYER_ID,
        type: "circle",
        source: ALL_SOURCE_ID,
        paint: {
          "circle-color": ["get", "assetColor"],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 4, 14, 7, 18, 9],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.5,
          "circle-opacity": 0.9,
        },
        layout: {
          visibility: "none",
        },
      });

      map.addLayer({
        id: ALL_POINT_LABEL_LAYER_ID,
        type: "symbol",
        source: ALL_SOURCE_ID,
        minzoom: 11.8,
        layout: {
          "text-field": ["get", "assetLabel"],
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size": 10,
          "text-offset": [0, 1.35],
          "text-anchor": "top",
          "text-allow-overlap": false,
          "text-ignore-placement": false,
          visibility: "none",
        },
        paint: {
          "text-color": "#334155",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5,
        },
      });

      map.addLayer({
        id: POINT_LABEL_LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        minzoom: 11.8,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "text-field": ["get", "assetLabel"],
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size": 10,
          "text-offset": [0, 1.4],
          "text-anchor": "top",
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#0f172a",
          "text-halo-width": 1.5,
        },
      });

      setMapReady(true);
      map.resize();
    });
    map.on("error", (event) => {
      // eslint-disable-next-line no-console
      console.error("Mapbox map error", event?.error || event);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [center, token, zoom]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return undefined;
    }

    const map = mapRef.current;
    const source = map.getSource(SOURCE_ID);
    const allSource = map.getSource(ALL_SOURCE_ID);
    if (!source || !allSource) {
      return undefined;
    }

    source.setData(featureCollection);
    allSource.setData(featureCollection);
    if (map.getLayer(CLUSTER_COUNT_LAYER_ID)) {
      map.setLayoutProperty(CLUSTER_COUNT_LAYER_ID, "text-field", clusterCountExpression);
    }

    const bounds = getBounds(plots);
    if (bounds) {
      if (plots.length === 1) {
        map.easeTo({
          center: [plots[0].lng, plots[0].lat],
          zoom: Math.max(zoom, 14),
          duration: 400,
        });
      } else {
        map.fitBounds(bounds, {
          padding: { top: 80, bottom: 80, left: 80, right: 80 },
          duration: 450,
        });
      }
    }

    return undefined;
  }, [clusterCountExpression, featureCollection, mapReady, plots, zoom]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return undefined;
    }

    const map = mapRef.current;
    const clusteredVisibility = clusterEnabled ? "visible" : "none";
    const allPointsVisibility = clusterEnabled ? "none" : "visible";

    [CLUSTER_LAYER_ID, CLUSTER_COUNT_LAYER_ID, POINT_LAYER_ID, POINT_LABEL_LAYER_ID].forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", clusteredVisibility);
      }
    });
    [ALL_POINT_LAYER_ID, ALL_POINT_LABEL_LAYER_ID].forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", allPointsVisibility);
      }
    });

    return undefined;
  }, [clusterEnabled, mapReady]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return undefined;
    }

    const map = mapRef.current;

    const handleClusterClick = (event) => {
      const features = map.queryRenderedFeatures(event.point, { layers: [CLUSTER_LAYER_ID] });
      const clusterId = features[0]?.properties?.cluster_id;
      const source = map.getSource(SOURCE_ID);
      if (clusterId === undefined || !source?.getClusterExpansionZoom) {
        return;
      }

      source.getClusterExpansionZoom(clusterId, (error, expansionZoom) => {
        if (error || !features[0]?.geometry?.coordinates) {
          return;
        }
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: expansionZoom,
          duration: 450,
        });
      });
    };

    const handlePointClick = (event) => {
      const feature = event.features?.[0];
      const plotId = String(feature?.properties?.id || "");
      const plot = plotsById.get(plotId);
      if (plot) {
        onSelectPlot(plot);
      }
    };

    const setPointer = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const clearPointer = () => {
      map.getCanvas().style.cursor = "";
    };

    map.on("click", CLUSTER_LAYER_ID, handleClusterClick);
    map.on("click", POINT_LAYER_ID, handlePointClick);
    map.on("click", ALL_POINT_LAYER_ID, handlePointClick);
    map.on("click", ALL_POINT_LABEL_LAYER_ID, handlePointClick);
    map.on("mouseenter", CLUSTER_LAYER_ID, setPointer);
    map.on("mouseleave", CLUSTER_LAYER_ID, clearPointer);
    map.on("mouseenter", POINT_LAYER_ID, setPointer);
    map.on("mouseleave", POINT_LAYER_ID, clearPointer);
    map.on("mouseenter", ALL_POINT_LAYER_ID, setPointer);
    map.on("mouseleave", ALL_POINT_LAYER_ID, clearPointer);
    map.on("mouseenter", ALL_POINT_LABEL_LAYER_ID, setPointer);
    map.on("mouseleave", ALL_POINT_LABEL_LAYER_ID, clearPointer);

    return () => {
      map.off("click", CLUSTER_LAYER_ID, handleClusterClick);
      map.off("click", POINT_LAYER_ID, handlePointClick);
      map.off("click", ALL_POINT_LAYER_ID, handlePointClick);
      map.off("click", ALL_POINT_LABEL_LAYER_ID, handlePointClick);
      map.off("mouseenter", CLUSTER_LAYER_ID, setPointer);
      map.off("mouseleave", CLUSTER_LAYER_ID, clearPointer);
      map.off("mouseenter", POINT_LAYER_ID, setPointer);
      map.off("mouseleave", POINT_LAYER_ID, clearPointer);
      map.off("mouseenter", ALL_POINT_LAYER_ID, setPointer);
      map.off("mouseleave", ALL_POINT_LAYER_ID, clearPointer);
      map.off("mouseenter", ALL_POINT_LABEL_LAYER_ID, setPointer);
      map.off("mouseleave", ALL_POINT_LABEL_LAYER_ID, clearPointer);
    };
  }, [mapReady, onSelectPlot, plotsById]);

  useEffect(() => {
    if (!mapRef.current) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      mapRef.current?.resize();
    }, 220);

    return () => window.clearTimeout(timer);
  }, [mapReady, panelOpen]);

  if (!token) {
    return (
      <div className={classes.root}>
        <div className={classes.empty}>
          <div className={classes.emptyInner}>
            `REACT_APP_MAPBOX_ACCESS_TOKEN` が未設定です。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div ref={mapContainerRef} className={classes.map} />
      <label className={classes.clusterToggle}>
        <input
          type="checkbox"
          checked={clusterEnabled}
          onChange={(event) => setClusterEnabled(event.target.checked)}
        />
        クラスター表示
      </label>
      {!mapReady && (
        <div className={classes.empty}>
          <div className={classes.emptyInner}>地図を読み込み中です。</div>
        </div>
      )}
    </div>
  );
};

PlotMapboxMap.propTypes = {
  plots: PropTypes.array.isRequired,
  onSelectPlot: PropTypes.func.isRequired,
  panelOpen: PropTypes.bool,
  initialCenter: PropTypes.array,
  initialZoom: PropTypes.number,
};

export default PlotMapboxMap;
